import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import Link, { LocalizedNavLink as NavLink } from "./LocalizedLink";
import LanguageSelector from "./LanguageSelector";
import { useI18n } from "../i18n/I18nProvider";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "../components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
} from "../components/ui/sheet";
import { ChevronDown, Menu } from "lucide-react";
import { api } from "../lib/api";

const navLinkBase =
  "text-[14px] tracking-[0.22em] uppercase text-[#1A1A1A] hover:text-[#C5A880] transition-colors duration-300";

const DEFAULTS = {
  brand_name: "Finca Cortesín",
  brand_subtitle: "New Developments",
  nav_developments: "Developments",
  nav_explore: "Explore",
  nav_about: "About",
  nav_blog: "News & Blog",
  nav_contact: "Contact",
};

const devLinks = [
  { label: "nav_dev_all", to: "/developments", testId: "nav-dev-all" },
  { label: "nav_dev_apartments", to: "/developments?category=apartments", testId: "nav-dev-apartments" },
  { label: "nav_dev_penthouses", to: "/developments?category=penthouses", testId: "nav-dev-penthouses" },
  { label: "nav_dev_villas", to: "/developments?category=villas", testId: "nav-dev-villas" },
  { label: "nav_dev_frontline", to: "/categories/frontline-golf", testId: "nav-dev-frontline" },
  { label: "nav_dev_seaview", to: "/categories/sea-view", testId: "nav-dev-seaview" },
];

const exploreLinks = [
  { label: "nav_explore_hotel", to: "/explore/hotel", testId: "nav-explore-hotel" },
  { label: "nav_explore_restaurants", to: "/explore/restaurants", testId: "nav-explore-restaurants" },
  { label: "nav_explore_beach", to: "/explore/beach-club", testId: "nav-explore-beach" },
  { label: "nav_explore_spa", to: "/explore/spa", testId: "nav-explore-spa" },
  { label: "nav_explore_golf", to: "/explore/golf-course", testId: "nav-explore-golf" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [h, setH] = useState(DEFAULTS);
  const location = useLocation();
  const { t } = useI18n();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/pages/header");
        setH({ ...DEFAULTS, ...data });
      } catch (_err) { /* fallback */ }
    })();
  }, []);

  // hide on admin routes
  if (location.pathname.startsWith("/admin")) return null;

  const navItems = [
    { label: h.nav_about, to: "/about", testId: "nav-about" },
    { label: h.nav_blog, to: "/blog", testId: "nav-blog" },
    { label: h.nav_contact, to: "/contact", testId: "nav-contact" },
  ];

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-[#F9F6F0]/95 backdrop-blur-md border-b border-[#E5DFD5]" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 flex items-center justify-between h-20">
        <Link to="/" data-testid="brand-link" className="flex flex-col leading-tight">
          <span className="font-serif text-2xl text-[#1A1A1A]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {h.brand_name}
          </span>
          <span className="text-[10px] tracking-[0.3em] uppercase text-[#73706B]">{h.brand_subtitle}</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          <DropdownMenu>
            <DropdownMenuTrigger data-testid="nav-developments-trigger" className={`${navLinkBase} flex items-center gap-1 outline-none`}>
              {h.nav_developments} <ChevronDown className="w-3 h-3" strokeWidth={1.5} />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="rounded-none border border-[#E5DFD5] bg-[#F9F6F0] py-3 px-0 min-w-[240px]">
              {devLinks.map((l) => (
                <DropdownMenuItem key={l.to} asChild className="rounded-none focus:bg-[#EFE9DC] focus:text-[#1A1A1A] cursor-pointer">
                  <Link to={l.to} data-testid={l.testId} className="block w-full px-5 py-2.5 text-[14px] tracking-[0.2em] uppercase text-[#1A1A1A] hover:text-[#C5A880]">
                    {t(l.label)}
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger data-testid="nav-explore-trigger" className={`${navLinkBase} flex items-center gap-1 outline-none`}>
              {h.nav_explore} <ChevronDown className="w-3 h-3" strokeWidth={1.5} />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="rounded-none border border-[#E5DFD5] bg-[#F9F6F0] py-3 px-0 min-w-[220px]">
              {exploreLinks.map((l) => (
                <DropdownMenuItem key={l.to} asChild className="rounded-none focus:bg-[#EFE9DC] focus:text-[#1A1A1A] cursor-pointer">
                  <Link to={l.to} data-testid={l.testId} className="block w-full px-5 py-2.5 text-[14px] tracking-[0.2em] uppercase text-[#1A1A1A] hover:text-[#C5A880]">
                    {t(l.label)}
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {navItems.map((item) => (
            <NavLink key={item.to} to={item.to} data-testid={item.testId} className={({ isActive }) => `${navLinkBase} ${isActive ? "text-[#C5A880]" : ""}`}>
              {item.label}
            </NavLink>
          ))}
          <LanguageSelector testIdPrefix="nav-lang" />
        </nav>

        {/* Mobile */}
        <div className="lg:hidden flex items-center gap-4">
          <LanguageSelector testIdPrefix="mobile-lang" />
          <Sheet>
            <SheetTrigger data-testid="mobile-menu-trigger" aria-label="Open menu" className="text-[#1A1A1A] p-2">
              <Menu className="w-5 h-5" strokeWidth={1.5} />
            </SheetTrigger>
            <SheetContent side="right" className="bg-[#F9F6F0] border-l border-[#E5DFD5] w-[85vw] sm:w-[420px] rounded-none overflow-y-auto">
              <SheetTitle className="sr-only">{t("site_navigation")}</SheetTitle>
              <div className="pt-10 flex flex-col gap-1">
                <span className="text-[10px] tracking-[0.3em] uppercase text-[#73706B] mb-4">{h.nav_developments}</span>
                {devLinks.map((l) => (
                  <Link key={l.to} to={l.to} data-testid={`mobile-${l.testId}`} className="block py-3 text-base text-[#1A1A1A] hover:text-[#C5A880] border-b border-[#E5DFD5]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    {t(l.label)}
                  </Link>
                ))}
                <span className="text-[10px] tracking-[0.3em] uppercase text-[#73706B] mt-8 mb-4">{h.nav_explore}</span>
                {exploreLinks.map((l) => (
                  <Link key={l.to} to={l.to} data-testid={`mobile-${l.testId}`} className="block py-3 text-base text-[#1A1A1A] hover:text-[#C5A880] border-b border-[#E5DFD5]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    {t(l.label)}
                  </Link>
                ))}
                <span className="text-[10px] tracking-[0.3em] uppercase text-[#73706B] mt-8 mb-4">{t("more")}</span>
                {navItems.map((item) => (
                  <Link key={item.to} to={item.to} data-testid={`mobile-${item.testId}`} className="block py-3 text-base text-[#1A1A1A] hover:text-[#C5A880] border-b border-[#E5DFD5]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    {item.label}
                  </Link>
                ))}
                <Link to="/contact?request=price-list" data-testid="mobile-cta-price-list" className="mt-8 bg-[#1A1A1A] text-[#F9F6F0] px-6 py-4 text-[11px] tracking-[0.25em] uppercase text-center hover:bg-[#C5A880] transition-colors">
                  {t("request_price_list")}
                </Link>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
